import { isPeriodicRuleComplete, parseLocalDate, PERIODIC_FORM, sortWeekdays } from './periodicReturnRule'

/**
 * Story 182 (issue #3015, RN40) — the readable sentence of a periodic return rule, in the user's language.
 *
 * Composition (frozen in 03-dominio.md §Resumen legible):
 *  1. each day has its OWN summary key ("Monday (summary)" ...), whose text carries the article in the
 *     natural form of the language ("el lunes", "am Montag", "Monday");
 *  2. one day -> the day; several -> all but the last joined by a FIXED ", " and the last through the
 *     "{{items}} and {{last}}" key. Days in week order, whatever order the rule stores them in;
 *  3. the list goes into the template of the form; `time` is shown as is; `start` is the series start
 *     formatted from its own digits (never `new Date('yyyy-MM-dd')`, which is UTC and a day early west of it);
 *  4. the first letter of the sentence is upper-cased in the active language (RELATIVE templates begin with
 *     the day, whose key may be lower case).
 *
 * Pure: the translator and the date formatter come in as parameters.
 */
const SUMMARY_DAY_KEYS = {
  MO: 'Monday (summary)',
  TU: 'Tuesday (summary)',
  WE: 'Wednesday (summary)',
  TH: 'Thursday (summary)',
  FR: 'Friday (summary)',
  SA: 'Saturday (summary)',
  SU: 'Sunday (summary)'
}

const LIST_SEPARATOR = ', '

const joinDays = (names, t_) => {
  if (names.length === 1) {
    return names[0]
  }
  return t_('{{items}} and {{last}}', {
    items: names.slice(0, -1).join(LIST_SEPARATOR),
    last: names[names.length - 1]
  })
}

const capitalize = (sentence, language) => sentence.charAt(0).toLocaleUpperCase(language) + sentence.slice(1)

/**
 * @param {Object|null} rule - the `periodicRule` of the job's `externalReturn`
 * @param {{t_: function, formatDate: function(Date): string, language: string}} environment
 * @returns {string} the sentence, or '' when the rule is absent, incomplete or of a form the client does not know
 */
export const periodicReturnSummary = (rule, { t_, formatDate, language }) => {
  if (!isPeriodicRuleComplete(rule)) {
    return ''
  }
  const days = joinDays(sortWeekdays(rule.weekdays).map((code) => t_(SUMMARY_DAY_KEYS[code])), t_)
  let sentence
  if (rule.form === PERIODIC_FORM.RELATIVE) {
    sentence = rule.weekInterval === 1
      ? t_('{{weekday}} of the week after dispatch, at {{time}}', { weekday: days, time: rule.time })
      : t_('{{weekday}} {{weeks}} weeks after the dispatch week, at {{time}}', { weekday: days, weeks: rule.weekInterval, time: rule.time })
  } else {
    const start = formatDate(parseLocalDate(rule.seriesStart))
    sentence = rule.weekInterval === 1
      ? t_('Every week on {{weekdays}} at {{time}}, from {{start}}', { weekdays: days, time: rule.time, start })
      : t_('Every {{weeks}} weeks on {{weekdays}} at {{time}}, from {{start}}', { weekdays: days, weeks: rule.weekInterval, time: rule.time, start })
  }
  return capitalize(sentence, language)
}

export default periodicReturnSummary
